import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Heart, Quote, ShieldCheck } from "lucide-react";
import { useDonate } from "../context/DonateContext";
import { api, fileUrl } from "../lib/api";
import { STATS, FOCUS_AREAS, HERO_IMAGE, TRUST } from "../data/content";

// Simple crossfade slider — no external carousel dependency
const HeroCarousel = ({ slides }) => {
  const images = slides.length ? slides : null;
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!images || images.length < 2) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % images.length), 5000);
    return () => clearInterval(id);
  }, [images]);

  useEffect(() => {
    if (images && index >= images.length) setIndex(0);
  }, [images, index]);

  if (!images) {
    return <img src={HERO_IMAGE} alt="Rural education" className="absolute inset-0 w-full h-full object-cover" />;
  }

  const active = images[index] || images[0];

  return (
    <div className="absolute inset-0" data-testid="hero-carousel">
      {images.map((s, i) => (
        <img
          key={s.id}
          src={fileUrl(s.image_path)}
          alt={s.title || "Foundation slide"}
          data-testid={`hero-slide-${s.id}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === index ? "opacity-100" : "opacity-0"}`}
        />
      ))}
      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {images.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setIndex(i)}
              data-testid={`hero-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-2.5 rounded-full transition-all ${i === index ? "w-8 bg-amber-400" : "w-2.5 bg-white/50 hover:bg-white/80"}`}
            />
          ))}
        </div>
      )}
      {active.title && (
        <div className="absolute bottom-16 left-4 sm:left-10 z-20 max-w-md">
          <p className="font-display text-white text-xl sm:text-2xl font-bold drop-shadow-lg">{active.title}</p>
          {active.subtitle && <p className="text-slate-200 text-sm mt-1 drop-shadow">{active.subtitle}</p>}
        </div>
      )}
    </div>
  );
};

const accent = {
  blue: { ring: "ring-blue-100", bg: "bg-blue-50", text: "text-blue-700", chip: "bg-blue-600" },
  emerald: { ring: "ring-emerald-100", bg: "bg-emerald-50", text: "text-emerald-700", chip: "bg-emerald-600" },
  amber: { ring: "ring-amber-100", bg: "bg-amber-50", text: "text-amber-700", chip: "bg-amber-600" },
};

const HomePage = () => {
  const { openDonate } = useDonate();
  const [preview, setPreview] = useState([]);
  const [slides, setSlides] = useState([]);

  useEffect(() => {
    api.get("/gallery").then((r) => setPreview(r.data.slice(0, 4))).catch(() => {});
    api.get("/hero-slides").then((r) => setSlides(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <HeroCarousel slides={slides} />
        <div className="absolute inset-0 hero-overlay pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="max-w-2xl fade-up">
            <span className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/40 text-amber-300 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full">
              <ShieldCheck className="h-4 w-4" /> Registered Public Charitable Trust
            </span>
            <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.08] tracking-tight">
              Compassion in action for a <span className="text-amber-400">dignified rural India</span>
            </h1>
            <p className="mt-6 text-lg text-slate-200 leading-relaxed max-w-xl">
              {TRUST.tagline} Inspired by Lord Buddha's values of compassion, wisdom and equality —
              serving education, healthcare and social justice in Aurangabad, Bihar.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <button
                onClick={openDonate}
                data-testid="hero-donate-btn"
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-7 py-3.5 rounded-full shadow-lg hover:shadow-xl transition-all"
              >
                <Heart className="h-5 w-5 fill-white" /> Donate Now
              </button>
              <a
                href="#focus-areas"
                data-testid="hero-explore-btn"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur text-white border border-white/30 font-semibold px-7 py-3.5 rounded-full transition-all"
              >
                Explore Our Work <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-[#0F2942]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => {
            const Icon = Icons[s.icon] || Icons.Star;
            return (
              <div key={s.label} className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                  <Icon className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <p className="font-display text-2xl sm:text-3xl font-extrabold text-white">{s.value}</p>
                  <p className="text-xs sm:text-sm text-slate-400">{s.label}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Mission */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-xs uppercase tracking-widest font-semibold text-emerald-700">Our Mission</span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
              Every child educated. Every family cared for. Every voice heard.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Lord Buddha Awareness And Development Foundation is dedicated to reaching free education,
              better health and dignified living opportunities to the deprived, backward and poor
              sections of society across rural and semi-urban Bihar.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Rooted in the values of Karuna (compassion), Sadbhav (harmony) and Shanti (peace), we work
              actively on the ground for social justice, women empowerment and youth development.
            </p>
          </div>
          <div className="relative">
            <div className="bg-gradient-to-br from-emerald-600 to-blue-800 rounded-3xl p-10 text-white shadow-2xl">
              <Quote className="h-10 w-10 text-amber-400 mb-4" />
              <p className="font-serif-accent text-2xl leading-relaxed italic">
                "Peace comes from within. Dignity comes when every child learns, every mother is
                healthy, and every voice is heard."
              </p>
              <p className="mt-6 text-sm text-emerald-100 font-semibold">— Our Guiding Philosophy</p>
            </div>
          </div>
        </div>
      </section>

      {/* Focus areas */}
      <section id="focus-areas" className="bg-slate-50 py-20 lg:py-28 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-widest font-semibold text-emerald-700">What We Do</span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold text-slate-900">Our Core Focus Areas</h2>
            <p className="mt-4 text-slate-600">Three pillars through which we create lasting change on the ground.</p>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-7">
            {FOCUS_AREAS.map((f) => {
              const Icon = Icons[f.icon] || Icons.Star;
              const a = accent[f.color];
              return (
                <div
                  key={f.id}
                  data-testid={`focus-card-${f.id.replace("_", "-")}`}
                  className="card-lift bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm flex flex-col"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img src={f.image} alt={f.title} className="w-full h-full object-cover" />
                    <span className={`absolute top-3 left-3 ${a.chip} text-white text-[11px] font-semibold px-3 py-1 rounded-full`}>
                      {f.tag}
                    </span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className={`h-11 w-11 rounded-xl ${a.bg} ring-4 ${a.ring} flex items-center justify-center mb-4`}>
                      <Icon className={`h-5 w-5 ${a.text}`} />
                    </div>
                    <h3 className="font-display text-lg font-bold text-slate-900">{f.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{f.summary}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery preview */}
      {preview.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-xs uppercase tracking-widest font-semibold text-emerald-700">Moments</span>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold text-slate-900">From The Field</h2>
            </div>
            <Link to="/gallery" className="hidden sm:inline-flex items-center gap-2 text-emerald-700 font-semibold hover:gap-3 transition-all">
              View Gallery <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {preview.map((g) => (
              <div key={g.id} className="relative rounded-xl overflow-hidden aspect-square group gallery-card">
                <img src={fileUrl(g.image_path)} alt={g.event_name} className="w-full h-full object-cover gallery-img" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                  <p className="text-white text-sm font-semibold">{g.event_name}</p>
                </div>
              </div>
            ))}
          </div>
          <Link to="/gallery" className="mt-8 sm:hidden inline-flex items-center gap-2 text-emerald-700 font-semibold">
            View Gallery <ArrowRight className="h-5 w-5" />
          </Link>
        </section>
      )}

      {/* CTA */}
      <section className="bg-[#0F2942] py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
            Your one small step can change a life.
          </h2>
          <p className="mt-4 text-slate-300 max-w-xl mx-auto">
            Join our mission — as a volunteer or by supporting a child's education and a family's health.
          </p>
          <button
            onClick={openDonate}
            className="mt-8 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-[#0F2942] font-bold px-8 py-4 rounded-full shadow-lg transition-all"
          >
            <Heart className="h-5 w-5 fill-[#0F2942]" /> Donate Today
          </button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
